/**
 * Collection Browser
 * Tab row switches between collections, fetching product grids via
 * Shopify's native collection JSON endpoint — no Section Rendering needed.
 */

(function () {
  'use strict';

  class CollectionBrowser {
    constructor(el) {
      this.el       = el;
      this.tabs     = Array.from(el.querySelectorAll('[data-cb-tab]'));
      this.panel    = el.querySelector('#cb-panel');
      this.grid     = el.querySelector('[data-cb-grid]');
      this.seeMore  = el.querySelector('[data-cb-see-more]');
      this.empty    = el.querySelector('.collection-browser__empty');

      if (!this.tabs.length || !this.panel) return;

      this.activeTab    = this.tabs[0];
      this.currentFetch = null;
      this.cache        = new Map(); // keyed by tab handle, stores rendered products

      this._bindTabs();

    
      // Populate the initial New Arrivals grid on load
      this._switchTab(this.activeTab, true);
    }

    /* ------------------------------------------------------------------
       Bind tab click handlers
    ------------------------------------------------------------------ */
    _bindTabs() {
      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => {
          if (tab === this.activeTab) return;
          this._switchTab(tab);
        });

        // Keyboard: left/right arrow to navigate tabs
        tab.addEventListener('keydown', (e) => {
          const idx = this.tabs.indexOf(tab);
          if (e.key === 'ArrowRight') {
            e.preventDefault();
            this.tabs[(idx + 1) % this.tabs.length].focus();
          } else if (e.key === 'ArrowLeft') {
            e.preventDefault();
            this.tabs[(idx - 1 + this.tabs.length) % this.tabs.length].focus();
          }
        });
      });
    }

    /* ------------------------------------------------------------------
       Switch to a tab — update UI then fetch products
    ------------------------------------------------------------------ */
    async _switchTab(tab, isInitialLoad = false) {
      // Update tab active states
      this.activeTab.classList.remove('collection-browser__tab--active');
      this.activeTab.setAttribute('aria-selected', 'false');
      tab.classList.add('collection-browser__tab--active');
      tab.setAttribute('aria-selected', 'true');
      this.activeTab = tab;

      // Keep the active tab scrolled into view on mobile (BUT NOT ON PAGE LOAD!)
      if (!isInitialLoad) {
        tab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      }

      const handle = tab.dataset.cbTab;
      const label  = tab.dataset.cbLabel;
      const url    = tab.dataset.cbUrl;

      // Update See More link immediately
      if (this.seeMore) {
        this.seeMore.href = url;
        this.seeMore.textContent = '';
        this.seeMore.appendChild(document.createTextNode(`See All ${label}`));
        // Re-append the arrow SVG
        const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svg.setAttribute('width', '16');
        svg.setAttribute('height', '16');
        svg.setAttribute('viewBox', '0 0 16 16');
        svg.setAttribute('fill', 'none');
        svg.setAttribute('aria-hidden', 'true');
        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        path.setAttribute('d', 'M3 8h10M9 4l4 4-4 4');
        path.setAttribute('stroke', 'currentColor');
        path.setAttribute('stroke-width', '1.5');
        path.setAttribute('stroke-linecap', 'round');
        path.setAttribute('stroke-linejoin', 'round');
        svg.appendChild(path);
        this.seeMore.appendChild(svg);
      }

      // Serve from cache if available — instant, no loading state needed
      if (this.cache.has(handle)) {
        this._renderGrid(this.cache.get(handle));
        return;
      }

      // Show loading state for fresh fetches
      this._setLoading(true);

      // Cancel any in-flight fetch
      if (this.currentFetch) this.currentFetch.abort();
      this.currentFetch = new AbortController();

      try {
        let products;
        if (handle === 'new-arrivals') {
          products = await this._fetchNewArrivals(this.currentFetch.signal);
        } else {
          products = await this._fetchCollection(handle, this.currentFetch.signal);
        }

        this.cache.set(handle, products); // store for instant re-access
        this._renderGrid(products);
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('[CollectionBrowser] fetch failed:', err);
          this._renderGrid([]);
        }
      } finally {
        this.currentFetch = null;
        this._setLoading(false);
      }
    }

    /* ------------------------------------------------------------------
       Fetch products for a specific collection handle
    ------------------------------------------------------------------ */
    async _fetchCollection(handle, signal) {
      const url = `/collections/${handle}/products.json?limit=12`;
      const res = await fetch(url, { signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      return data.products || [];
    }

    /* ------------------------------------------------------------------
       Fetch newest products from the configured New Arrivals collections.
       Handles are passed via data-cb-na-handles as a pipe-delimited string.
       Falls back to all products if no handles are set.
    ------------------------------------------------------------------ */
    async _fetchNewArrivals(signal) {
      const handlesAttr = this.tabs[0]?.dataset.cbNaHandles || '';
      const handles = handlesAttr ? handlesAttr.split('|').filter(Boolean) : [];

      if (!handles.length) {
        // Fallback — no collections configured, use all products
        const res = await fetch(`/collections/all/products.json?limit=12&sort_by=created-descending`, { signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        return data.products || [];
      }

      // Fetch all configured collections in parallel
      const results = await Promise.all(
        handles.map(async (handle) => {
          const res = await fetch(`/collections/${handle}/products.json?limit=250`, { signal });
          if (!res.ok) return [];
          const data = await res.json();
          return data.products || [];
        })
      );

      // Merge, deduplicate by id, sort by created_at descending, take top 12
      const seen = new Set();
      const merged = results
        .flat()
        .filter((p) => {
          if (seen.has(p.id)) return false;
          seen.add(p.id);
          return true;
        })
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 12);

      return merged;
    }

    /* ------------------------------------------------------------------
       Render products into the grid
    ------------------------------------------------------------------ */
    _renderGrid(products) {
      const grid  = this.grid;
      const empty = this.empty;

      if (!products.length) {
        grid.innerHTML = '';
        if (empty) empty.hidden = false;
        return;
      }

      if (empty) empty.hidden = true;

      grid.innerHTML = products.map((p) => {
        const image       = p.images?.[0];
        const secondImage = p.images?.[1];
        const imgTag = image
          ? `<img
               src="${this._shopifyImageUrl(image.src, 600)}"
               srcset="${this._shopifySrcset(image.src)}"
               sizes="(min-width: 990px) 25vw, (min-width: 600px) 33vw, 50vw"
               alt="${this._escape(image.alt || p.title)}"
               class="cb-card__img cb-card__img--primary"
               loading="lazy"
               width="${image.width || 600}"
               height="${image.height || 600}"
             >${secondImage ? `<img
               src="${this._shopifyImageUrl(secondImage.src, 600)}"
               srcset="${this._shopifySrcset(secondImage.src)}"
               sizes="(min-width: 990px) 25vw, (min-width: 600px) 33vw, 50vw"
               alt="${this._escape(secondImage.alt || p.title)}"
               class="cb-card__img cb-card__img--hover"
               loading="lazy"
               width="${secondImage.width || 600}"
               height="${secondImage.height || 600}"
             >` : ''}`
          : `<div class="cb-card__placeholder"><svg viewBox="0 0 525 525" class="placeholder-svg"><rect width="525" height="525" fill="#f4f4f4"/></svg></div>`;

        const price        = this._money(p.variants?.[0]?.price);
        const comparePrice = p.variants?.[0]?.compare_at_price;
        const onSale       = comparePrice && parseFloat(comparePrice) > parseFloat(p.variants?.[0]?.price);

        const priceHtml = onSale
          ? `<s class="cb-card__price-compare">${this._money(comparePrice)}</s>
             <span class="cb-card__price-current">${price}</span>`
          : `<span class="cb-card__price-current">${price}</span>`;

        const badge = onSale ? `<span class="cb-card__badge">Sale</span>` : '';

        return `<li class="collection-browser__item">
          <a href="/products/${p.handle}" class="cb-card" aria-label="${this._escape(p.title)}">
            <div class="cb-card__media">
              ${imgTag}
              ${badge}
            </div>
            <div class="cb-card__info">
              <p class="cb-card__vendor">${this._escape(p.vendor)}</p>
              <h3 class="cb-card__title">${this._escape(p.title)}</h3>
              <div class="cb-card__price">${priceHtml}</div>
            </div>
          </a>
        </li>`;
      }).join('');
    }

    /* ------------------------------------------------------------------
       Loading state toggle
    ------------------------------------------------------------------ */
    _setLoading(on) {
      this.panel.classList.toggle('is-loading', on);
      this.panel.setAttribute('aria-busy', String(on));
      if (this.grid) this.grid.classList.toggle('is-loading', on);
    }

    /* ------------------------------------------------------------------
       Helpers
    ------------------------------------------------------------------ */
    _money(price) {
      // Shopify's /products.json returns prices as decimal strings e.g. "29.99"
      // NOT as cents — so no division needed
      if (!price) return '$0.00';
      return parseFloat(price).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
    }

    _escape(str) {
      return String(str || '').replace(/[&<>"']/g, (c) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
      })[c]);
    }

    // Shopify image URL resizing
    _shopifyImageUrl(src, width) {
      return src.replace(/(\.[^.]+)$/, `_${width}x$1`);
    }

    _shopifySrcset(src) {
      return [300, 400, 500, 600]
        .map((w) => `${this._shopifyImageUrl(src, w)} ${w}w`)
        .join(', ');
    }
  }

  /* ------------------------------------------------------------------
     Boot
  ------------------------------------------------------------------ */
  function init() {
    document.querySelectorAll('.collection-browser').forEach((el) => {
      if (!el._cbInstance) el._cbInstance = new CollectionBrowser(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (e) => {
    const el = e.target.querySelector('.collection-browser');
    if (el) el._cbInstance = new CollectionBrowser(el);
  });

  document.addEventListener('shopify:section:unload', (e) => {
    const el = e.target.querySelector('.collection-browser');
    if (el) delete el._cbInstance;
  });
})();
